import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { ApiError } from '../../../api/client';
import { errorMessage } from '../../../lib/notify';
import { docLink, docType } from './labels';

export type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const toneColor = (t: Theme, tone: Tone): string => {
  const p = (t.vars ?? t).palette;
  return tone === 'neutral' ? p.text.secondary : p[tone].main;
};

/** Compact KPI tile used in detail and list headers. */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  loading,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  loading?: boolean;
}) {
  return (
    <Card sx={{ position: 'relative', overflow: 'hidden', height: '100%' }}>
      <Box
        aria-hidden
        sx={(t) => ({
          position: 'absolute',
          right: -36,
          top: -36,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `radial-gradient(circle, color-mix(in srgb, ${toneColor(t, tone)} 14%, transparent), transparent 70%)`,
        })}
      />
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
            {label}
          </Typography>
          {icon && (
            <Box
              sx={(t) => ({
                width: 34,
                height: 34,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                color: toneColor(t, tone),
                bgcolor: `color-mix(in srgb, ${toneColor(t, tone)} 10%, transparent)`,
                '& svg': { fontSize: 19 },
              })}
            >
              {icon}
            </Box>
          )}
        </Stack>
        <Typography
          variant="h4"
          component="div"
          className="tabular"
          sx={{ mt: 0.75, fontWeight: 700 }}
        >
          {loading ? <Skeleton width="60%" /> : value}
        </Typography>
        {hint !== undefined && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {loading ? <Skeleton width="40%" /> : hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** Responsive grid of stat cards. */
export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        mb: 2.5,
      }}
    >
      {children}
    </Box>
  );
}

/** A titled card section. */
export function SectionCard({
  title,
  subtitle,
  action,
  children,
  flush,
  sx,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** No inner padding (for tables). */
  flush?: boolean;
  sx?: SxProps<Theme>;
}) {
  return (
    <Card sx={sx}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          px: 2.5,
          pt: 2.25,
          pb: flush ? 2 : 0,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: flush ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="div">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {flush ? children : <Box sx={{ p: 2.5 }}>{children}</Box>}
    </Card>
  );
}

/** Label/value pairs in a responsive grid. */
export function InfoGrid({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
      }}
    >
      {items.map((i) => (
        <Box key={i.label} sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">
            {i.label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 550 }}>
            {i.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button
      size="small"
      startIcon={<ArrowBackRounded />}
      onClick={() => void navigate(to)}
      sx={{ color: 'text.secondary', mb: 1.5, ml: -1, displayPrint: 'none' }}
    >
      {label}
    </Button>
  );
}

/** Load failure: the message, the request id for support, and a retry. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <Card>
      <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            borderRadius: '18px',
            display: 'grid',
            placeItems: 'center',
            color: 'error.main',
            bgcolor: 'color-mix(in srgb, var(--mui-palette-error-main) 10%, transparent)',
            '& svg': { fontSize: 30 },
          }}
        >
          <ErrorOutlineRounded />
        </Box>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {notFound ? 'Not found' : 'Couldn’t load this'}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 440, mx: 'auto' }}>
          {notFound ? 'It may have been removed, or the link is wrong.' : errorMessage(error)}
        </Typography>
        {requestId && (
          <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 1 }}>
            Request id {requestId}
          </Typography>
        )}
        {onRetry && !notFound && (
          <Button variant="outlined" sx={{ mt: 3 }} onClick={onRetry}>
            Try again
          </Button>
        )}
      </Box>
    </Card>
  );
}

/** Skeleton shaped like a detail page: header, four tiles, a table card. */
export function DetailSkeleton() {
  return (
    <Box aria-busy>
      <Skeleton width={120} height={28} />
      <Skeleton width={280} height={48} sx={{ mb: 2 }} />
      <StatGrid>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={104} />
        ))}
      </StatGrid>
      <Skeleton variant="rounded" height={320} />
    </Box>
  );
}

/** A document number linking to its screen when one exists. */
export function DocLink({
  type,
  id,
  number,
  showType,
}: {
  type: string;
  id: string;
  number: string | null | undefined;
  showType?: boolean;
}) {
  const to = docLink(type, id);
  const text = number ?? docType(type);
  return (
    <Box component="span">
      {to ? (
        <Link
          component={RouterLink}
          to={to}
          underline="hover"
          sx={{ fontWeight: 600 }}
          onClick={(e) => e.stopPropagation()}
        >
          {text}
        </Link>
      ) : (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {text}
        </Box>
      )}
      {showType && number && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
          {docType(type)}
        </Typography>
      )}
    </Box>
  );
}

/** Two-line cell: a strong primary line and a muted secondary line. */
export function Cell2({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap component="div">
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

/** Thin allocated-vs-total bar for list rows and summaries. */
export function AllocationBar({ value, tone = 'success' }: { value: number; tone?: Tone }) {
  return (
    <Box
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={{ height: 6, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}
    >
      <Box
        sx={(t) => ({
          height: '100%',
          width: `${String(value)}%`,
          borderRadius: 99,
          bgcolor: toneColor(t, tone),
          transition: 'width 200ms cubic-bezier(0.2,0,0,1)',
        })}
      />
    </Box>
  );
}

/** A date that also names the party on phones, where the party column is hidden. */
export function DateWithParty({ date, party }: { date: string; party: string }) {
  return (
    <>
      <Box component="span" sx={{ display: { sm: 'none' } }}>
        {party} ·{' '}
      </Box>
      {date}
    </>
  );
}
